<template>
    <div>
        <card-header title="Edit Quote" icon="fa-pencil"/>

        <card-body>
            <quote-form v-if="quote.courses" :quote="quote" @loading="loading" @mounted="onFormMounted"/>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column is-1">
                    <b-button type="is-warning is-outlined" size="is-small" @click="deleteQuote"><i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Delete Quote?</span></b-button>
                </div>
                <div class="column has-text-right">
                    <!-- Create Cart button for cart enrollment quotes -->
                    <b-button
                        type="is-info"
                        size="is-small"
                        @click="$openCard('prepare-cart', {quoteId: quote.id}, card)"
                        class="mr-3"
                        :disabled="dirty || quote.status !== 'SUBMITTED'"
                        v-if="(currentUser.role === 'dev' || currentUser.role === 'admin') && quote.enrollmentType === 'cart_enrollment'"
                    ><i class="fas fa-shopping-cart mr-2"></i>Create Cart
                    </b-button>
                    <!-- Create Plan button for payment plan quotes -->
                    <b-button
                        type="is-info"
                        size="is-small"
                        @click="$openCard('prepare-plan', {quoteId: quote.id}, card)"
                        class="mr-3"
                        :disabled="dirty || quote.status !== 'SUBMITTED'"
                        v-if="(currentUser.role === 'dev' || currentUser.role === 'admin') && quote.enrollmentType === 'payment_plan'"
                    ><i class="fas fa-money-check-alt mr-2"></i>Create Plan
                    </b-button>
                    <b-button
                        :type="{'is-primary': quote.status !== 'SUBMITTED', 'is-danger': quote.status === 'SUBMITTED'}"
                        size="is-small"
                        @click="submit"
                        :disabled="isButtonDisabled"
                    ><i class="fas fa-check mr-2"></i><span v-if="quote.status === 'SUBMITTED' && (currentUser.role !== 'admin' && currentUser.role !== 'dev')">Submit Quote for Processing</span><span v-else>Save Quote</span>
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardFooter from "@/TIER/components/CardFooter";
    import QuoteForm from "@/cards/reseller/QuoteForm";

    import {client as http} from '../../http_client';

    import async from 'async';
    import {mapGetters} from "vuex";

    export default {
        components: {QuoteForm, CardFooter, CardToolbar, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                quote: {user: {}},
                confirmDelete: false,
                dirty: null,
                loaded: false
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            isButtonDisabled() {
                if (this.quote.enrollmentType === 'cart_enrollment') {
                    // Cart enrollment: just need user and clock hours > 0
                    return !this.quote.user || !this.quote.hours || this.quote.hours <= 0;
                } else {
                    // Payment plan: full validation including term >= 2 and <= 48
                    return !this.quote.valid || !this.quote.user || !this.quote.title || this.quote.hours < 10 || !this.quote.term || this.quote.term < 2 || this.quote.term > 48 || !this.quote.total || this.quote.total <= 0;
                }
            }
        },
        methods: {
            loadQuote: function() {
                this.$emit('loading', true);

                async.series([
                    (cb) => {
                        http.get('courses/quotable').then(response => {
                            this.courses = response.data;
                            cb();
                        });
                    },
                    (cb) => {
                        http.get('quotes/' + this.props.quoteId, {force: true}).then(response => {
                            response.data.courses = [];
                            response.data.valid = true;
                            this.quote = response.data;

                            // Initialize enrollmentType based on term
                            this.quote.enrollmentType = (this.quote.term === 0) ? 'cart_enrollment' : 'payment_plan';

                            // Only parse defer_on for payment plans
                            if (this.quote.enrollmentType === 'payment_plan' && this.quote.defer_on) {
                                this.quote.defer_day = parseInt(this.quote.defer_on.split('-')[2]).toString(); // drop leading zero
                                this.quote.defer_month = this.quote.defer_on.split('-')[1];
                                this.quote.defer_year = this.quote.defer_on.split('-')[0];
                            } else {
                                this.quote.defer_day = 1;
                                this.quote.defer_month = null;
                                this.quote.defer_year = null;
                            }

                            this.quote.courses = JSON.parse(this.quote.course_choices);

                            for (let i = 0; i < this.quote.courses.length; i++) {
                                const choice = this.quote.courses[i];
                                for (let j = 0; j < this.courses.length; j++) {
                                    const course = this.courses[j];
                                    if (course.id === choice.course_id) {
                                        choice.course = course;
                                        break;
                                    }
                                }
                            }
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    }
                ], err => {
                    this.$emit('loading', false);
                    // Don't set loaded here - wait for QuoteForm to mount
                });
            },
            onFormMounted() {
                // QuoteForm has mounted and computed properties have run
                // Now we can start tracking changes
                this.$nextTick(() => {
                    this.loaded = true;
                    this.dirty = null;
                });
            },
            submit: function() {
                this.$emit('loading', true);

                this.quote.user_id = this.quote.user.id;
                delete this.quote.user;

                this.quote.course_choices = [];
                for (let i = 0; i < this.quote.courses.length; i++) {
                    let course = this.quote.courses[i];
                    this.quote.course_choices.push({
                        course_id: course.course_id,
                        course_name: course.course_name,
                        config_id: course.config_id,
                        custom1: course.custom1,
                        custom2: course.custom2
                    });
                }
                this.quote.course_choices = JSON.stringify(this.quote.course_choices);
                delete this.quote.courses;

                http.put('quotes/' + this.props.quoteId, this.quote, {force: true}).then(response => {
                    this.$reloadCard('reseller-quotes');
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    console.error('Error saving quote:', err);
                    this.$emit('loading', false);
                });
            },
            deleteQuote() {
                if (this.confirmDelete) {
                    this.$emit('loading', true);
                    http.delete('quotes/' + this.props.quoteId).then(response => {
                        this.$reloadCard('reseller-quotes');
                        this.$saveAlert(false);
                        this.$closeCard(this.card);
                    }).catch(err => {

                    });
                } else {
                    this.confirmDelete = true;
                    setTimeout(() => {
                        this.confirmDelete = false;
                    }, 3000);
                }
            },
            loading: function(val) {
                this.$emit('loading', val);
            }
        },
        watch: {
            'props.quoteId': function() {
                this.loaded = false;
                this.dirty = null;
                this.loadQuote();
            },
            'quote': {
                deep: true,
                handler: function() {
                    // Only track changes after the quote has fully loaded
                    if (this.loaded) {
                        this.dirty = (this.dirty !== null);
                        if (!this.$TIER.saveAlert.active && this.dirty)
                            this.$saveAlert(true, 'Are you done editing this quote? Unsaved changes will be lost.');
                    }
                }
            },
            'quote.user': {
                deep: true,
                handler: function() {
                    // Also track changes to the user object
                    if (this.loaded) {
                        this.dirty = (this.dirty !== null);
                        if (!this.$TIER.saveAlert.active && this.dirty)
                            this.$saveAlert(true, 'Are you done editing this quote? Unsaved changes will be lost.');
                    }
                }
            }
        },
        mounted() {
            this.loadQuote();
        }
    };
</script>
